/* iceberg-ui */
import { Button, IconWithVariant } from '@auviknetworks/iceberg-ui';

/* MUI */
import { Typography } from '@mui/material';

/*  Shared  */
import { FontSizes } from 'application/Shared/Constants';

/* React */
import React, { cloneElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

/* Internal */
import { Item, Menu } from './Menu';

/**
 * Represents an array of menu items for the dropdown
 */
export type DropdownMenu = Item[];

interface DropdownProps {
    /** data-testid of the dropdown button */
    'data-testid'?: string;
    /** data-trackid of the dropdown button */
    'data-trackid'?: string;
    /** The translation key for the button label */
    labelKey?: string;
    /** An array of menu items to be displayed in the dropdown */
    menu: Item[];
    /** The children to be displayed in the dropdown, if not provided, the button will be displayed */
    children?: React.ReactNode;
}

/**
 * A dropdown component that displays a button with a label and a chevron icon,
 * which opens a menu when clicked.
 *
 * @example
 * const menu = [
 *   { label: 'Option 1', onClick: () => {} },
 *   { label: 'Option 2', onClick: () => {} }
 * ];
 *
 * <Dropdown labelKey="common.select" menu={menu} />
 */
export const Dropdown = ({
    'data-testid': dataTestid = 'dropdownMenuBtn',
    'data-trackid': dataTrackid = 'dropdownMenu',
    labelKey,
    menu,
    children
}: DropdownProps): JSX.Element => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>();
    const { t } = useTranslation();

    const Child = children
        ? cloneElement(children as React.ReactElement, {
              onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
                  setAnchorEl(event.currentTarget);
              }
          })
        : null;

    return (
        <>
            {Child && Child}
            {!Child && (
                <Button
                    data-testid={dataTestid}
                    data-trackid={`${dataTrackid}_displayMenuBtn`}
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        setAnchorEl(event.currentTarget);
                    }}
                >
                    {labelKey && (
                        <Typography component="span" flex="1">
                            {t(labelKey)}
                        </Typography>
                    )}
                    <IconWithVariant
                        src="ChevronDown"
                        size={FontSizes[2]}
                        wrapperProps={{
                            borderLeft: '1px solid',
                            marginLeft: '16px',
                            paddingLeft: '16px'
                        }}
                    />
                </Button>
            )}
            {Boolean(anchorEl) && (
                <Menu
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        horizontal: 'center',
                        vertical: 'bottom'
                    }}
                    menu={menu}
                    MenuListProps={{
                        'aria-label': `dropdown.${labelKey}`
                    }}
                    onClose={() => setAnchorEl(undefined)}
                    open={Boolean(anchorEl)}
                    slotProps={{
                        paper: { sx: { paddingX: 0, paddingY: '8px' } }
                    }}
                    transformOrigin={{
                        horizontal: 'center',
                        vertical: 'top'
                    }}
                />
            )}
        </>
    );
};
